import { AW_COOKIE, SESSION_NAME } from '../constants/metadata';
import Cookie from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { CookieSessionContext } from './types';
import { CustomError } from '../errors/CustomError';
import { handleError, tryCatchWrapper } from '@/utilities/errors/handleError';
import { ErrorType } from '@/utilities/errors/types';
import { DdEventCategory } from '../logger/datadog/types';
import { getRegion } from './cookieGetters';
import { useStore } from '@/store/useStore';
import { setSessionCookie } from '../setters/cookieSetters';

type ParsedAccountSession = Omit<
  CookieSessionContext,
  'userId' | 'region' | 'token'
> & {
  created?: string | Date;
  userId?: string;
  region?: Lowercase<CookieSessionContext['region']>;
  token?: string;
};

const parseAccountSessionRaw = (
  accountSession: string,
): ParsedAccountSession | null => {
  const parsedAccountSession = {} as ParsedAccountSession;

  try {
    Object.assign(parsedAccountSession, { ...JSON.parse(accountSession) });
  } catch (error: unknown) {
    handleError(
      {
        type: ErrorType.PARSE,
        payload: {
          message: '[GW][SESSION]: Failed to get sessionContext',
          reason: 'Failed to parse account session',
        },
        details: error,
      },
      {
        useCase: DdEventCategory.Initialization,
      },
    );
  }

  const authJwt = parsedAccountSession?.authToken?.jwt;

  if (!authJwt) return null;

  return parsedAccountSession;
};

const readAccountSessionRaw = (
  source: 'cookie' | 'localStorage',
): string | null => {
  if (source === 'cookie') {
    const raw = Cookie.get(AW_COOKIE.X_ACCOUNT_SESSION);
    return raw && typeof raw === 'string' ? raw : null;
  }

  const raw = localStorage.getItem(SESSION_NAME);
  return raw && typeof raw === 'string' ? raw : null;
};

const getSessionCreatedMs = (session: ParsedAccountSession): number => {
  const created =
    session.authToken?.created ??
    session.created ??
    session.loginToken?.created;
  if (!created) return 0;

  const timestamp = Date.parse(String(created));
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

/**
 * When lobby and game-window both hold Account UM state, prefer the fresher
 * refreshable session blob (cookie vs localStorage).
 */
export const pickSafeLocalAccountSession = (): CookieSessionContext | null => {
  const { isXsell } = useStore.getState().runtime.wrapperType;

  if (isXsell) {
    const authToken = Cookie.get(AW_COOKIE.AUTH_TOKEN);
    const loginToken = Cookie.get(AW_COOKIE.LOGIN_TOKEN);
    const sessionId = Cookie.get(AW_COOKIE.SESSION_ID);
    const userId = Cookie.get(AW_COOKIE.USER_ID);

    if (!authToken || !loginToken || !sessionId || !userId) return null;

    const session = enrichAccountSession({
      authToken: { jwt: authToken ?? '' },
      loginToken: { jwt: loginToken ?? '' },
      sessionId: sessionId ?? '',
      userId: userId ?? '',
    });
    // Set the cookies only for current domain to avoid wrongfully picking the wrong ones on the next pass
    setSessionCookie(AW_COOKIE.AUTH_TOKEN, authToken ?? '');
    setSessionCookie(AW_COOKIE.LOGIN_TOKEN, loginToken ?? '');
    setSessionCookie(AW_COOKIE.SESSION_ID, sessionId ?? '');
    setSessionCookie(AW_COOKIE.USER_ID, session.userId ?? '');

    return session;
  }
  const cookieRaw = readAccountSessionRaw('cookie');
  const localRaw = readAccountSessionRaw('localStorage');

  if (!cookieRaw && !localRaw) return null;

  let cookieSession: ParsedAccountSession | null = null;
  let localSession: ParsedAccountSession | null = null;

  if (cookieRaw) {
    cookieSession = parseAccountSessionRaw(cookieRaw);
  }
  if (localRaw) {
    localSession = parseAccountSessionRaw(localRaw);
  }

  if (!cookieSession && !localSession) return null;

  const picked =
    cookieSession && localSession
      ? getSessionCreatedMs(cookieSession) >= getSessionCreatedMs(localSession)
        ? cookieSession
        : localSession
      : (cookieSession ?? localSession);

  if (!picked) return null;

  return enrichAccountSession(picked);
};

const enrichAccountSession = (
  parsedAccountSession: ParsedAccountSession,
): CookieSessionContext => {
  const userId = getUserId(parsedAccountSession.authToken.jwt);

  return {
    ...parsedAccountSession,
    userId,
    region: parsedAccountSession.region ?? getRegion(),
    token: parsedAccountSession.token ?? '',
  } as CookieSessionContext;
};

export const getAWAuthTokens = () => {
  const accountSession = pickSafeLocalAccountSession();
  return {
    authToken: accountSession?.authToken?.jwt ?? '',
    loginToken: accountSession?.loginToken?.jwt ?? '',
  };
};

export const getSessionContext = async (): Promise<{
  sessionContext: CookieSessionContext;
} | null> => {
  const region = getRegion();

  const localAccountSession = await safeGetLocalAccountSession();

  if (
    localAccountSession &&
    localAccountSession.loginToken &&
    localAccountSession.authToken &&
    localAccountSession.userId &&
    localAccountSession.sessionId
  ) {
    return {
      sessionContext: {
        ...localAccountSession,
        region,
        token: localAccountSession.token || '',
      } as CookieSessionContext,
    };
  }

  return null;
};

export const getLocalAccountSession = (): CookieSessionContext | null => {
  return pickSafeLocalAccountSession();
};

export const getUserId = (authToken: string) => {
  try {
    const decodedJwt = jwtDecode(authToken);
    const userId = decodedJwt.sub;
    if (!userId) {
      throw new CustomError('User ID is missing');
    }
    return userId.toString();
  } catch (error: unknown) {
    handleError(
      {
        type: ErrorType.PARSE,
        payload: {
          message: '[GW][SESSION]: Failed to get sessionContext',
          reason: 'Failed to decode JWT for userId',
        },
        details: error,
      },
      {
        useCase: DdEventCategory.Initialization,
      },
    );
  }
};

const safeGetLocalAccountSession = () => {
  return tryCatchWrapper(() => getLocalAccountSession(), {
    errorType: ErrorType.SESSION,
    errorMessage: '[GW][SESSION]: Failed to get local account session',
    errorReason: 'Error retrieving session data',
    useCase: DdEventCategory.Initialization,
  });
};
